/** Pure squarified-ish treemap layout used by the "Sales by market group" chart. */

export type TreemapBounds = {
  height: number
  width: number
  x: number
  y: number
}

export type TreemapGroup = {
  count: number
  market: string
  total: number
}

export type TreemapRect = TreemapBounds & TreemapGroup

export function layoutTreemap(groups: TreemapGroup[], bounds: TreemapBounds): TreemapRect[] {
  if (groups.length === 0) return []
  if (groups.length === 1) return [{ ...groups[0], ...bounds }]

  const total = totalTreemapSales(groups)
  const splitIndex = treemapSplitIndex(groups, total)
  const first = groups.slice(0, splitIndex)
  const second = groups.slice(splitIndex)
  const firstTotal = totalTreemapSales(first)

  if (bounds.width >= bounds.height) {
    const firstWidth = bounds.width * (firstTotal / total)
    return [
      ...layoutTreemap(first, { ...bounds, width: firstWidth }),
      ...layoutTreemap(second, {
        ...bounds,
        width: bounds.width - firstWidth,
        x: bounds.x + firstWidth,
      }),
    ]
  }

  const firstHeight = bounds.height * (firstTotal / total)
  return [
    ...layoutTreemap(first, { ...bounds, height: firstHeight }),
    ...layoutTreemap(second, {
      ...bounds,
      height: bounds.height - firstHeight,
      y: bounds.y + firstHeight,
    }),
  ]
}

function treemapSplitIndex(groups: TreemapGroup[], total: number): number {
  let runningTotal = 0
  let bestDelta = Number.POSITIVE_INFINITY
  let bestIndex = 1

  groups.slice(0, -1).forEach((group, index) => {
    runningTotal += group.total
    const delta = Math.abs(total / 2 - runningTotal)

    if (delta < bestDelta) {
      bestDelta = delta
      bestIndex = index + 1
    }
  })

  return bestIndex
}

function totalTreemapSales(groups: TreemapGroup[]): number {
  return groups.reduce((sum, group) => sum + group.total, 0) || 1
}

export function truncateTileLabel(label: string, maxChars: number): string {
  if (label.length <= maxChars) return label
  return `${label.slice(0, Math.max(1, maxChars - 3))}...`
}
